import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip as WmsTooltip } from 'react-tooltip';
import LoaderSpinner from '../common/Loader';
import AlertDialogModal from '../common/AlertDialog';
import BatchPagination from '../batch/BatchPagination';
import PricingTemplateFilter from './PricingTemplateFilter';
import { fetchAllCustomPricingTemplates, deleteCustomPricingTemplate } from '../../store/actions/customPricingTemplate.action';
import { CustomPricingTemplateColumns } from '../../constant';

const CustomPricingTemplates = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { showAccountingMenu } = useSelector((state) => state.accountingReducer);
    const { templateList, templateListStatus, totalRecords } = useSelector((state) => state.customPricingTemplateReducer);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);
    const [sortColumn, setSortColumn] = useState('createdDate');
    const [sortOrder, setSortOrder] = useState('ASCENDING');
    const [filterData, setFilterData] = useState({});
    const [initialFilterData, setInitialFilterData] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null });

    const totalPages = Math.ceil(totalRecords / pageSize) || 1;

    const returnPath = location?.state?.returnPath;
    const returnState = location?.state?.returnState;

    const userDetails = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const userValue = userDetails?.Value || {};

    const buildRequestPayload = (page, sort, order, filters = {}) => ({
        pageIndex: page || currentPage,
        pageSize,
        sortColumn: sort || sortColumn || '',
        sortOrder: (order || sortOrder || 'ASCENDING').toUpperCase(),
        searchkey: '',
        companySellerId: userValue?.companySellerId || 0,
        role: userValue?.roleId || 0,
        isSuperAdmin: !!userValue?.isSuperAdmin,
        tenantId: userValue?.tenantId || 0,
        isBillingCycle: false,
        userEmail: userValue?.userEmailId || userValue?.email || '',
        warehouseList: [],
        sellerIdList: [],
        companyIdList: [],
        serviceTypes: [],
        subServiceTypes: [],
        skuList: [],
        ...filters,
    });

    const fetchData = (page, sort, order, filters) => {
        dispatch(fetchAllCustomPricingTemplates(buildRequestPayload(page, sort, order, filters)));
    };

    useEffect(() => {
        const preAppliedFilters = location?.state?.preAppliedFilters;
        if (preAppliedFilters && Object.keys(preAppliedFilters).length) {
            setFilterData(preAppliedFilters);
            setInitialFilterData(preAppliedFilters);
            fetchData(1, sortColumn, sortOrder, preAppliedFilters);
            return;
        }

        fetchData(1, sortColumn, sortOrder, filterData);
    }, []);

    const handleSort = (column) => {
        const newOrder = sortColumn === column && sortOrder === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING';
        setSortColumn(column);
        setSortOrder(newOrder);
        fetchData(currentPage, column, newOrder, filterData);
    };

    const getSortIcon = (order) => (order === 'ASCENDING' ? 'fa fa-sort-asc' : 'fa fa-sort-desc');

    const handleSortingClass = (column, isIcon) => {
        if (sortColumn !== column) return isIcon ? 'fa fa-sort' : '';
        return isIcon ? getSortIcon(sortOrder) : 'sorted';
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchData(page, sortColumn, sortOrder, filterData);
    };

    const handleFilter = (filters) => {
        setFilterData(filters);
        setCurrentPage(1);
        fetchData(1, sortColumn, sortOrder, filters);
    };

    const handleReset = () => {
        setFilterData({});
        setCurrentPage(1);
        fetchData(1, sortColumn, sortOrder, {});
    };

    const handleDelete = () => {
        if (!deleteModal.id) return;
        dispatch(deleteCustomPricingTemplate(deleteModal.id)).then((success) => {
            setDeleteModal({ show: false, id: null });
            if (success) {
                fetchData(currentPage, sortColumn, sortOrder, filterData);
            }
        });
    };

    const renderCommaSeparatedLines = (value) => {
        if (!value) return <div className="multiValueLine">-</div>;
        const rawValue = String(value);
        const splitEntries = rawValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        if (!splitEntries.length) return <div className="multiValueLine">-</div>;

        const entries = splitEntries.map((entry, index) => {
            const hasCommaAfter = index < splitEntries.length - 1;
            return hasCommaAfter ? `${entry},` : entry;
        });

        return (
            <>
                {entries.map((entry, index) => (
                    <div key={`${entry}-${index}`} className="multiValueLine">{entry}</div>
                ))}
            </>
        );
    };

    return (
        <div className={`${showAccountingMenu ? 'col-10' : 'col-12'}`}>
            <div className="main__tile_heading cus-btn-include-sec">
                <span>Custom Pricing Templates</span>
                <div className="top_right_button_container">
                    <button
                        className="btn cus-seconday-bg-btn top-btn"
                        onClick={() => navigate('/accounting/pricing-templates/template-new')}
                    >
                        <i className="fa fa-plus mr-1" aria-hidden="true"></i> Create New Template
                    </button>
                    {returnPath && (
                        <button
                            className="btn cus-primary-transparent-btn"
                            onClick={() => navigate(returnPath, { state: returnState })}
                        >
                            Back
                        </button>
                    )}
                </div>
            </div>

            <div className="cus-page-content-sec">
                <PricingTemplateFilter onFilter={handleFilter} onReset={handleReset} initialFilters={initialFilterData} />

                {templateListStatus === 'PENDING' && <LoaderSpinner />}

                <div className="table-responsive">
                    <table className="table table-bordered customPricingTemplateTable">
                        <thead>
                            <tr>
                                {CustomPricingTemplateColumns.map((col) => (
                                    <th
                                        key={col.label}
                                        className={col.key ? `cursorPointer ${handleSortingClass(col.key)}` : ''}
                                        onClick={() => col.key && handleSort(col.key)}
                                    >
                                        {col.label}
                                        {col.key && <i className={handleSortingClass(col.key, true)} aria-hidden="true"></i>}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {templateList.length === 0 && templateListStatus !== 'PENDING' && (
                                <tr><td colSpan={CustomPricingTemplateColumns.length} className="text-center">No records found</td></tr>
                            )}
                            {templateList.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.templateName}</td>
                                    <td>{item.warehouseName}</td>
                                    <td>{renderCommaSeparatedLines(item.companyName)}</td>
                                    <td>{renderCommaSeparatedLines(item.sellerName)}</td>
                                    <td>{item.serviceType}</td>
                                    <td>{renderCommaSeparatedLines(item.subServiceType)}</td>
                                    <td>{renderCommaSeparatedLines(item.skuIds)}</td>
                                    <td>{renderCommaSeparatedLines(item.carrierConnectNumbers)}</td>
                                    <td className="sticky">
                                        <button
                                            className="iconBtnListing cursorPointer mr-2"
                                            data-tooltip-id="view-tooltip"
                                            data-tooltip-content="View"
                                            onClick={() => navigate(`/accounting/pricing-templates/template-${item.id}`, { state: { isViewMode: true } })}
                                        >
                                            <i className="fa fa-eye" aria-hidden="true"></i>
                                        </button>
                                        <button
                                            className="iconBtnListing cursorPointer mr-2"
                                            data-tooltip-id="edit-tooltip"
                                            data-tooltip-content="Edit"
                                            onClick={() => navigate(`/accounting/pricing-templates/template-${item.id}`, { state: { isViewMode: false } })}
                                        >
                                            <i className="fa fa-pencil" aria-hidden="true"></i>
                                        </button>
                                        <button
                                            className="iconBtnListing cursorPointer"
                                            data-tooltip-id="delete-tooltip"
                                            data-tooltip-content="Delete"
                                            onClick={() => setDeleteModal({ show: true, id: item.id })}
                                        >
                                            <i className="fa fa-trash error-icon" aria-hidden="true"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <WmsTooltip id="view-tooltip" />
                <WmsTooltip id="edit-tooltip" />
                <WmsTooltip id="delete-tooltip" />

                {totalRecords > pageSize && (
                    <BatchPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>

            <AlertDialogModal
                modalShow={deleteModal.show}
                handleCancelDeleteAlert={() => setDeleteModal({ show: false, id: null })}
                handleYesDeleteAlert={handleDelete}
                headingText="Delete Template"
                messageText="Are you sure you want to delete this template?"
            />
        </div>
    );
};

export default CustomPricingTemplates;
